if (!customElements.get('sales-video-registration')) {
  customElements.define('sales-video-registration', class extends HTMLElement {
    static openDialogs = new Set();
    static previousOverflow = '';

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-preview-form]');
      this.trigger = this.querySelector('[data-preview-open]');
      if (!this.dialog || !this.form || !this.trigger || this.events) return;

      this.formArea = this.querySelector('[data-preview-form-area]');
      this.success = this.querySelector('[data-preview-success]');
      this.error = this.querySelector('[data-preview-error]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.querySelector('[data-preview-submit-label]');
      this.buttonText = this.submitLabel.textContent;
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.trigger.disabled = false;
      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-preview-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.cleanup();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]')]
          .filter((element) => element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.form.addEventListener('input', (event) => event.target.removeAttribute('aria-invalid'), options);
      this.querySelector('[data-preview-continue]').addEventListener('click', (event) => {
        if (event.defaultPrevented || !this.submitted) return;
        window.clearTimeout(this.redirectTimer);
        this.trackRedirect();
      }, options);

      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.blockId === this.dataset.blockId) this.open();
        }, options);
        document.addEventListener('shopify:block:deselect', (event) => {
          if (event.detail.blockId === this.dataset.blockId) this.close();
        }, options);
      }
    }

    open(opener = this.trigger) {
      if (this.dialog.open) return;
      this.redirectTracked = false;
      if (this.dataset.designMode !== 'true') {
        try {
          const destination = this.getDestination();
          if (this.submitted || localStorage.getItem(this.registrationKey(destination)) === '1') {
            this.showSuccess(destination);
            this.querySelector('[data-preview-success-heading]').textContent = this.success.dataset.returningHeading;
            this.querySelector('[data-preview-success-description]').textContent = this.success.dataset.returningText;
          }
        } catch {
          // Storage may be unavailable; keep the normal registration flow usable.
        }
      }
      this.opener = opener;
      this.opener.setAttribute('aria-controls', this.dialog.id);
      this.closing = false;
      this.dialog.showModal();
      const owner = this.constructor;
      if (!owner.openDialogs.size) {
        owner.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      owner.openDialogs.add(this);
      // Establish the starting style before the entrance transition.
      this.dialog.getBoundingClientRect();
      this.dialog.classList.add('is-visible');
      const focusTarget = this.submitted ? this.querySelector('[data-preview-success-message]') : this.form.elements.first_name;
      focusTarget.focus({ preventScroll: true });
      if (!this.submitted) this.track('lesson1_gate_view');
    }

    track(eventName) {
      if (this.dataset.designMode === 'true') return;
      const pageType = this.dataset.pageType;
      if (!['pdp', 'homepage'].includes(pageType)) return;
      // Only page context is sent; never include the form's email or name.
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, { page_type: pageType, transport_type: 'beacon' });
        } else {
          // GTM installations can forward these custom events to their GA4 tag.
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: eventName, page_type: pageType });
        }
      } catch { /* Analytics must not interrupt registration or navigation. */ }
      try {
        window.clarity = window.clarity || function () {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };
        window.clarity('set', 'page_type', pageType);
        window.clarity('set', eventName, pageType);
        window.clarity('event', eventName);
      } catch { /* Keep Clarity independent of the gate. */ }
    }

    trackRedirect() {
      if (this.redirectTracked) return;
      this.redirectTracked = true;
      this.track('lesson1_redirect');
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;
      this.cancelPending();
      this.dialog.classList.remove('is-visible');
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 360;
      this.closeTimer = window.setTimeout(() => {
        this.dialog.close();
        this.cleanup();
      }, duration);
    }

    cancelPending() {
      window.clearTimeout(this.redirectTimer);
      window.clearTimeout(this.requestTimer);
      this.request?.abort();
      this.request = null;
      this.setLoading(false);
    }

    cleanup() {
      this.cancelPending();
      window.clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-visible');
      this.closing = false;
      const owner = this.constructor;
      if (owner.openDialogs.delete(this) && !owner.openDialogs.size) {
        document.documentElement.style.overflow = owner.previousOverflow;
      }
      if (this.isConnected && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    setLoading(loading) {
      this.submitButton.disabled = loading;
      this.submitLabel.textContent = loading ? this.submitButton.dataset.loadingText : this.buttonText;
      this.form.setAttribute('aria-busy', String(loading));
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    getDestination() {
      const { companyId, listId, redirectUrl } = this.form.dataset;
      if (!companyId || !listId || !redirectUrl || redirectUrl.trim().startsWith('#')) throw new Error('Missing settings');
      const destination = new URL(redirectUrl, window.location.href);
      if (!['https:', 'http:'].includes(destination.protocol)) throw new Error('Invalid course URL');
      return destination;
    }

    registrationKey(destination) {
      const { companyId, listId } = this.form.dataset;
      return 'sowell:course-preview:v1:' + JSON.stringify([companyId, listId, destination.href]);
    }

    showSuccess(destination) {
      this.submitted = true;
      this.formArea.hidden = true;
      this.success.hidden = false;
      this.dialog.setAttribute('aria-labelledby', this.dialog.id + '-success-title');
      this.dialog.setAttribute('aria-describedby', this.dialog.id + '-success-description');
      this.querySelector('[data-preview-continue]').href = destination.href;
    }

    async submit(event) {
      event.preventDefault();
      if (this.request || this.submitted || this.closing || !this.dialog.open) return;
      this.error.hidden = true;
      const nameInput = this.form.elements.first_name;
      const emailInput = this.form.elements.email;
      nameInput.value = nameInput.value.trim();
      emailInput.value = emailInput.value.trim();
      const inputs = [nameInput, emailInput];
      inputs.forEach((input) => input.setAttribute('aria-invalid', String(!input.checkValidity())));
      const invalid = inputs.find((input) => !input.checkValidity());
      if (invalid) {
        this.showError(this.form.dataset.invalidText);
        invalid.focus();
        return;
      }

      if (this.dataset.designMode === 'true') {
        this.showError(this.form.dataset.editorText);
        return;
      }

      const { companyId, listId, source } = this.form.dataset;
      let destination;
      try {
        destination = this.getDestination();
      } catch {
        this.showError(this.form.dataset.configText);
        return;
      }

      this.setLoading(true);
      const request = new AbortController();
      this.request = request;
      this.requestTimer = window.setTimeout(() => request.abort(), 15000);
      try {
        // Public client endpoint: never put a private Klaviyo key in theme code.
        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          signal: request.signal,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: source,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: emailInput.value,
                      first_name: nameInput.value,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        });
        if (!response.ok) throw new Error('Registration failed');
        if (this.request !== request || !this.isConnected || !this.dialog.open) return;
        this.track('lesson1_gate_submit');

        try {
          // Remember acceptance only; no name or email is stored in this browser.
          localStorage.setItem(this.registrationKey(destination), '1');
        } catch {
          // A storage restriction must not prevent success or navigation.
        }
        this.showSuccess(destination);
        this.querySelector('[data-preview-success-message]').focus({ preventScroll: true });
        this.dialog.scrollTop = 0;
        // Leave time to read the success alert, with a manual link as a fallback.
        this.redirectTimer = window.setTimeout(() => {
          if (this.isConnected && this.dialog.open && !this.closing) {
            this.trackRedirect();
            window.location.assign(destination.href);
          }
        }, 2200);
      } catch {
        if (this.request === request && this.isConnected && this.dialog.open) this.showError(this.form.dataset.errorText);
      } finally {
        if (this.request === request) {
          window.clearTimeout(this.requestTimer);
          this.request = null;
          this.setLoading(false);
        }
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      if (!this.dialog || !this.form) return;
      this.cleanup();
      this.dialog.close();
    }
  });

  // Delegation also covers product sections re-rendered by the theme editor.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-course-preview-trigger]');
    if (!opener) return;
    const registration = document.querySelector('sales-video-registration');
    if (!registration?.dialog || !registration.isConnected) return;
    event.preventDefault();
    registration.open(opener);
  });
}
